import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { stytchService } from '../../utils/stytchService';
import { useContentSize, saveContentSize } from "../../hooks/useContentSize";
import { validateEmail } from "../../utils/validationUtils";
import { setAuthenticationSource, AuthenticationSource } from "../../store/authPreferencesSlice";
import UserLoginView from "./UserLoginView";
import LoadingUserComponent from "./LoadingUser";

type Props = {
  onEmailLoginSuccess: (methodId: string, email: string) => void;
};

export default function UserLoginContainer({ onEmailLoginSuccess }: Props) {
  const dispatch = useDispatch();

  const [email, setEmail] = useState(import.meta.env.VITE_DEFAULT_EMAIL || "");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { ref: contentRef, size: contentSize } = useContentSize(!loading);

  const getAuthenticationURL = () => {
    const currentUrl = window.location.origin;
    return `${currentUrl}/login`;
  };

  const saveCardSize = () => {
    saveContentSize(contentRef);
  };

  const onGoogleLogin = async () => {
    dispatch(setAuthenticationSource(AuthenticationSource.AssessmentGoogle));
    setError(null);
    setLoading(true);
    saveCardSize();
    const redirectUrl = getAuthenticationURL();
    try {
      const response = await stytchService.startGoogleOAuth(redirectUrl);
      if (response && !response.success) {
        console.error("Google login failed:", response, response.error);
        setLoading(false);
        setError("Google login failed. Please try again.");
      }
    } catch (err) {
      console.error("Google login failed:", err);
      setLoading(false);
      setError("Google login failed. Please try again.");
    }
  };

  const onLinkedInLogin = async () => {
    dispatch(setAuthenticationSource(AuthenticationSource.AssessmentLinkedIn));
    setError(null);
    setLoading(true);
    saveCardSize();
    const redirectUrl = getAuthenticationURL();
    try {
      const response = await stytchService.startLinkedInOAuth(redirectUrl);
      if (response && !response.success) {
        console.error("LinkedIn login failed:", response, response.error);
        setLoading(false);
        setError("LinkedIn login failed. Please try again.");
      }
    } catch (err) {
      console.error("LinkedIn login failed:", err);
      setLoading(false);
      setError("LinkedIn login failed. Please try again.");
    }
  };

  const onGithubLogin = async () => {
    dispatch(setAuthenticationSource(AuthenticationSource.AssessmentGithub));
    setError(null);
    setLoading(true);
    saveCardSize();
    const redirectUrl = getAuthenticationURL();
    try {
      const response = await stytchService.startGithubOAuth(redirectUrl);
      if (response && !response.success) {
        console.error("GitHub login failed:", response, response.error);
        setLoading(false);
        setError("GitHub login failed. Please try again.");
      }
    } catch (err) {
      console.error("GitHub login failed:", err);
      setLoading(false);
      setError("GitHub login failed. Please try again.");
    }
  };

  const onEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(setAuthenticationSource(AuthenticationSource.AssessmentEmail));
    setError(null);
    setLoading(true);

    if (!validateEmail(email)) {
      setLoading(false);
      setError("Please enter a valid email address");
      return;
    }

    try {
      const res = await stytchService.startEmailLogin(email);
      if (res) {
        onEmailLoginSuccess(res.method_id, email);
      }
    } catch (err: any) {
      console.error("Stytch OTP error:", err);
      setLoading(false);
      setError("Failed to send verification code");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: contentSize?.height || "auto",
          minWidth: contentSize?.width || "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <LoadingUserComponent />
      </div>
    );
  }

  return (
    <div ref={contentRef} className="user-login-container">
      <UserLoginView
        email={email}
        error={error}
        onEmailChange={(e) => setEmail(e.target.value)}
        onEmailSubmit={onEmailSubmit}
        onGoogleLogin={onGoogleLogin}
        onLinkedInLogin={onLinkedInLogin}
        onGithubLogin={onGithubLogin}
      />
    </div>
  );
}
